import { forwardRef, useCallback, useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';

// ---------- Date mask: DD/MM/YYYY HH:mm ----------

export function formatDateMask(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 12);
  const d = digits.padEnd(0, ' ');
  let out = '';
  if (d.length > 0) out += d.slice(0, 2);
  if (d.length > 2) out += `/${d.slice(2, 4)}`;
  if (d.length > 4) out += `/${d.slice(4, 8)}`;
  if (d.length > 8) out += ` ${d.slice(8, 10)}`;
  if (d.length > 10) out += `:${d.slice(10, 12)}`;
  return out;
}

/**
 * Convert a masked "DD/MM/YYYY HH:mm" string to an ISO-8601 string (UTC).
 * Returns null when the input is incomplete or invalid.
 */
export function parseDateMask(masked: string): string | null {
  const m = masked.match(/^(\d{2})\/(\d{2})\/(\d{4})\s(\d{2}):(\d{2})$/);
  if (!m) return null;
  const [, dd, mm, yyyy, hh, mi] = m;
  const d = new Date(
    Number(yyyy),
    Number(mm) - 1,
    Number(dd),
    Number(hh),
    Number(mi),
  );
  if (Number.isNaN(d.getTime())) return null;
  // Validate the date round-trip (rejects 31/02, 32/01, etc.).
  if (
    d.getFullYear() !== Number(yyyy)
    || d.getMonth() !== Number(mm) - 1
    || d.getDate() !== Number(dd)
    || d.getHours() !== Number(hh)
    || d.getMinutes() !== Number(mi)
  ) return null;
  return d.toISOString();
}

/**
 * Take an ISO-8601 string and render it as DD/MM/YYYY HH:mm in the local
 * timezone (matches what the user would type).
 */
export function isoToDateMask(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface DateMaskInputProps {
  value: string; // ISO-8601 or ''
  onChange: (iso: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
}

export const DateMaskInput = forwardRef<HTMLInputElement, DateMaskInputProps>(
  ({ value, onChange, placeholder = 'DD/MM/YYYY HH:mm', className, id }, ref) => {
    // Local display state for the masked text. Kept in sync with parent ISO
    // when the parent's ISO resolves to something the mask can represent.
    const [display, setDisplay] = useState(() => isoToDateMask(value));

    // Re-sync if the parent's ISO changes while the user isn't editing
    // (e.g., after loading an existing campaign).
    useEffect(() => {
      const parentMask = isoToDateMask(value);
      // If the parent's ISO matches what's already displayed (round-tripped),
      // skip. This prevents clobbering user input mid-typing.
      if (parentMask !== display && parseDateMask(display) !== value) {
        setDisplay(parentMask);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const masked = formatDateMask(e.target.value);
        setDisplay(masked);
        const iso = parseDateMask(masked);
        if (iso) {
          onChange(iso);
        } else if (masked === '') {
          onChange('');
        }
        // Partial mask: don't push invalid data upward; parent keeps its
        // previous ISO until the mask becomes complete.
      },
      [onChange],
    );

    return (
      <Input
        ref={ref}
        id={id}
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        className={className}
        value={display}
        onChange={handleChange}
      />
    );
  },
);
DateMaskInput.displayName = 'DateMaskInput';

// ---------- Currency mask: R$ 1.000,00 / $ 1,000.00 ----------

export function formatCentsBRL(cents: number | null | undefined): string {
  const n = Number(cents || 0) / 100;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

export function formatCentsUSD(cents: number | null | undefined): string {
  const n = Number(cents || 0) / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/**
 * Parse digits out of a masked currency string and return the integer cents.
 * Rule: whatever digits the user types, treat the last 2 as the fractional
 * cents. Handles both BRL ("1.000,00") and USD ("1,000.00") inputs because
 * we strip non-digits entirely.
 */
export function parseMaskedCurrencyToCents(masked: string): number {
  const digits = masked.replace(/\D/g, '');
  if (!digits) return 0;
  return parseInt(digits, 10);
}

interface CurrencyMaskInputProps {
  valueCents: number | null;
  onChangeCents: (cents: number | null) => void;
  currency: 'BRL' | 'USD';
  allowEmpty?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
}

export const CurrencyMaskInput = forwardRef<HTMLInputElement, CurrencyMaskInputProps>(
  (
    {
      valueCents,
      onChangeCents,
      currency,
      allowEmpty = false,
      placeholder,
      className,
      id,
    },
    ref,
  ) => {
    const format = currency === 'USD' ? formatCentsUSD : formatCentsBRL;
    const displayValue = valueCents == null
      ? (allowEmpty ? '' : format(0))
      : format(valueCents);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const stripped = raw.replace(/\D/g, '');
        if (stripped === '') {
          onChangeCents(allowEmpty ? null : 0);
          return;
        }
        onChangeCents(parseMaskedCurrencyToCents(stripped));
      },
      [onChangeCents, allowEmpty],
    );

    return (
      <Input
        ref={ref}
        id={id}
        type="text"
        inputMode="numeric"
        placeholder={placeholder ?? format(0)}
        className={className}
        value={displayValue}
        onChange={handleChange}
      />
    );
  },
);
CurrencyMaskInput.displayName = 'CurrencyMaskInput';
